import { useEffect, useRef, useState } from "preact/hooks";
import { useText } from "preact-i18n";
import { useDebounce } from "react-use";
import type MvAdressResult from "models/MvAddressResult";
import { getLocations } from "services/api";
import {
    buildNearbyAddressesQuery,
    RequestParams,
} from "services/addressQueries";
import { useLatestRequest } from "services/useLatestRequest";
import type { AreaProps } from "./Area";

const errorMessage = (error: unknown, fallback: string) =>
    error instanceof Error ? error.message || fallback : fallback;

export const useAreaRequests = ({
    addressData,
    apiBasePath = "",
    countryCode,
    onChange,
    onSelect,
    onAddressesFound,
    startLoading,
    stopLoading,
}: AreaProps) => {
    const currentAddress = addressData.result ?? null;
    const params = addressData.inputParams;
    const [postalCode, setPostalCode] = useState(
        params.postalCode || currentAddress?.postcode || "",
    );
    const [streetName, setStreetName] = useState(
        params.streetName || currentAddress?.straatnaam || "",
    );
    const [houseNumber, setHouseNumber] = useState<string | number>(
        params.houseNumber || currentAddress?.huisnr || "",
    );
    const [error, setError] = useState("");
    const [fetchResults, setFetchResults] = useState<MvAdressResult[]>([]);
    const [showForm, setShowForm] = useState(!currentAddress);
    const { singular } = useText("general.messages.addressNotFound.singular");
    const { addressNotFoundForSelected } = useText(
        "general.messages.addressNotFoundForSelected",
    );
    const { addressesNotFoundForSelection } = useText(
        "general.messages.addressesNotFoundForSelection",
    );
    const resolvedCountry = countryCode ?? "nl";
    const previousAddressId = useRef(currentAddress?.id);
    const searchedDraft = useRef({ postalCode, streetName, houseNumber });

    const addressRequest = useLatestRequest<{
        addresses: MvAdressResult[];
        initialize: boolean;
        query: RequestParams;
    }>({
        startLoading,
        stopLoading,
        onResult: ({ addresses, initialize, query }) => {
            if (!addresses.length) {
                setError(
                    initialize
                        ? `${addressNotFoundForSelected} ${query.zipcode}, ${query.housenumber}${query.housenumber_extension_letter ? `${query.housenumber_extension_letter}${query.housenumber_extension_number || ""}` : ""}`
                        : singular,
                );
                if (initialize) setShowForm(true);
                return;
            }
            if (initialize) {
                onChange(addressData.id, addresses[0]);
                setShowForm(false);
                onSelect(addressData.id);
            } else setFetchResults(addresses);
        },
        onError: (error) => setError(errorMessage(error, singular)),
    });
    const objectsRequest = useLatestRequest<MvAdressResult[]>({
        startLoading,
        stopLoading,
        onResult: (addresses) => onAddressesFound(addressData.id, addresses),
        onError: (error) =>
            setError(errorMessage(error, addressesNotFoundForSelection)),
    });

    const getLocation = async (query: RequestParams, initialize = false) => {
        setError("");
        setFetchResults([]);
        await addressRequest.run(async (signal) => ({
            addresses: (await getLocations(apiBasePath, query, signal)).data,
            initialize,
            query,
        }));
    };

    // Invalidate requests as soon as inputs change, before the next debounce fires.
    useEffect(() => {
        addressRequest.cancel();
    }, [postalCode, streetName, houseNumber, apiBasePath]);
    useEffect(() => {
        objectsRequest.cancel();
    }, [params, currentAddress, apiBasePath]);

    useEffect(() => {
        if (previousAddressId.current !== currentAddress?.id) {
            previousAddressId.current = currentAddress?.id;
            if (currentAddress) {
                setPostalCode(currentAddress.postcode);
                setStreetName(currentAddress.straatnaam);
                setHouseNumber(currentAddress.huisnr);
                searchedDraft.current = {
                    postalCode: currentAddress.postcode,
                    streetName: currentAddress.straatnaam,
                    houseNumber: currentAddress.huisnr,
                };
            }
        }
    }, [currentAddress]);

    useEffect(() => {
        if (currentAddress || !postalCode || !houseNumber) return;
        const query: RequestParams = {
            c: "address",
            housenumber: houseNumber,
            zipcode: postalCode,
        };
        if (params.houseNumberExtensionLetter)
            query.housenumber_extension_letter =
                params.houseNumberExtensionLetter;
        if (params.houseNumberExtensionNumber)
            query.housenumber_extension_number =
                params.houseNumberExtensionNumber;
        if (params.countryCode) query.countryCode = params.countryCode;
        if (params.streetName) query.streetName = params.streetName;
        void getLocation(query, true);
    }, []);

    const [, cancelLookup] = useDebounce(
        () => {
            const previous = searchedDraft.current;
            if (
                houseNumber &&
                postalCode &&
                (resolvedCountry === "nl" || streetName) &&
                (previous.houseNumber !== houseNumber ||
                    previous.postalCode !== postalCode ||
                    previous.streetName !== streetName)
            ) {
                searchedDraft.current = { postalCode, streetName, houseNumber };
                const query: RequestParams = {
                    c: "address",
                    housenumber: houseNumber,
                    zipcode: postalCode,
                    countryCode: resolvedCountry,
                };
                if (resolvedCountry !== "nl" && streetName)
                    query.streetName = streetName;
                void getLocation(query);
            }
        },
        500,
        [postalCode, houseNumber, streetName],
    );

    const [, cancelObjects] = useDebounce(
        () => {
            if (!currentAddress) return;
            setError("");
            void objectsRequest.run(
                async (signal) =>
                    (
                        await getLocations(
                            apiBasePath,
                            buildNearbyAddressesQuery(
                                params,
                                currentAddress,
                                countryCode,
                            ),
                            signal,
                        )
                    ).data,
            );
        },
        1000,
        [params, currentAddress],
    );

    return {
        postalCode,
        setPostalCode,
        streetName,
        setStreetName,
        houseNumber,
        setHouseNumber,
        error,
        fetchResults,
        setFetchResults,
        showForm,
        setShowForm,
        isFetching: addressRequest.isPending || objectsRequest.isPending,
        cancelRequests: () => {
            cancelLookup();
            cancelObjects();
            addressRequest.cancel();
            objectsRequest.cancel();
        },
    };
};
